// src/pages/finance-portal/estimated-tax/EstimatedTax.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Tooltip,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import CalculateIcon from '@mui/icons-material/Calculate';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    SCHEDULED: { bg: '#dbeafe', color: '#1e40af' },
    PAID: { bg: '#dcfce7', color: '#166534' },
    LATE: { bg: '#fecaca', color: '#991b1b' },
};

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function EstimatedTax() {
    const { enqueueSnackbar } = useSnackbar();
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState(null);
    const [taxYear, setTaxYear] = useState(new Date().getFullYear().toString());

    // Payments state
    const [payments, setPayments] = useState([]);
    const [payTotal, setPayTotal] = useState(0);
    const [payPage, setPayPage] = useState(0);
    const [payRowsPerPage, setPayRowsPerPage] = useState(20);
    const [payLoading, setPayLoading] = useState(true);

    // Payment edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [editData, setEditData] = useState({
        paid_amount: '', paid_date: '', payment_method: '', confirmation: '',
    });
    const [saving, setSaving] = useState(false);

    // Calculator state
    const [calcForm, setCalcForm] = useState({
        projected_annual_income: '', projected_deductions: '', credits: '', withholding: '',
    });
    const [calcResult, setCalcResult] = useState(null);
    const [calculating, setCalculating] = useState(false);

    const fetchPayments = useCallback(async () => {
        setPayLoading(true);
        setError(null);
        try {
            const params = { page: payPage + 1, per_page: payRowsPerPage };
            if (taxYear) params.tax_year = taxYear;
            const res = await axiosInstance.get('/api/finance/tax/estimated-payments', { params });
            if (res.data.success) {
                setPayments(res.data.data || []);
                setPayTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load payments');
        } finally {
            setPayLoading(false);
        }
    }, [payPage, payRowsPerPage, taxYear]);

    useEffect(() => {
        if (activeTab === 0) fetchPayments();
    }, [activeTab, fetchPayments]);

    const openEditDialog = (pmt) => {
        setEditingPayment(pmt);
        setEditData({
            paid_amount: pmt.paid_amount?.toString() || '',
            paid_date: pmt.paid_date ? pmt.paid_date.split('T')[0] : '',
            payment_method: pmt.payment_method || '',
            confirmation: pmt.confirmation || '',
        });
        setEditOpen(true);
    };

    const handleSavePayment = async () => {
        setSaving(true);
        try {
            const payload = {
                paid_amount: parseFloat(editData.paid_amount) || 0,
                paid_date: editData.paid_date,
                payment_method: editData.payment_method,
                confirmation: editData.confirmation,
            };
            await axiosInstance.put(`/api/finance/tax/estimated-payments/${editingPayment.id}`, payload);
            enqueueSnackbar('Payment recorded', { variant: 'success' });
            setEditOpen(false);
            fetchPayments();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save payment', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCalculate = async () => {
        setCalculating(true);
        setCalcResult(null);
        try {
            const payload = {
                projected_annual_income: parseFloat(calcForm.projected_annual_income) || 0,
                projected_deductions: parseFloat(calcForm.projected_deductions) || 0,
                credits: parseFloat(calcForm.credits) || 0,
                withholding: parseFloat(calcForm.withholding) || 0,
                tax_year: parseInt(taxYear, 10),
            };
            const res = await axiosInstance.post('/api/finance/tax/estimated-payments/calculate', payload);
            if (res.data.success) {
                setCalcResult(res.data.data);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Calculation failed', { variant: 'error' });
        } finally {
            setCalculating(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CalendarTodayIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Estimated Tax</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Quarterly payments and tax calculator</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        size="small" label="Tax Year" type="number" value={taxYear}
                        onChange={e => { setTaxYear(e.target.value); setPayPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    />
                </Box>

                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.9rem' },
                    '& .Mui-selected': { color: ACCENT },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    <Tab label="Payments" />
                    <Tab label="Calculator" />
                </Tabs>

                {/* Payments Tab */}
                <TabPanel value={activeTab} index={0}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Quarter', 'Due Date', 'Estimated', 'Paid', 'Status', 'Actions'].map(h => (
                                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payLoading ? (
                                        [1, 2, 3, 4].map(i => (
                                            <TableRow key={i}>{[1, 2, 3, 4, 5, 6].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                        ))
                                    ) : payments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No estimated payments found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : payments.map(pmt => (
                                        <TableRow key={pmt.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>Q{pmt.quarter}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatDate(pmt.due_date)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatCurrency(pmt.estimated_amount)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatCurrency(pmt.paid_amount)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={getStatusLabel(pmt.status)} size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                        bgcolor: (STATUS_COLORS[pmt.status] || STATUS_COLORS.SCHEDULED).bg,
                                                        color: (STATUS_COLORS[pmt.status] || STATUS_COLORS.SCHEDULED).color,
                                                    }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Tooltip title="Record Payment">
                                                    <IconButton size="small" onClick={() => openEditDialog(pmt)}>
                                                        <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div" count={payTotal} page={payPage}
                            onPageChange={(_, p) => setPayPage(p)}
                            rowsPerPage={payRowsPerPage}
                            onRowsPerPageChange={e => { setPayRowsPerPage(parseInt(e.target.value, 10)); setPayPage(0); }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ '& *': { fontFamily } }}
                        />
                    </Card>
                </TabPanel>

                {/* Calculator Tab */}
                <TabPanel value={activeTab} index={1}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2 }}>Estimated Tax Calculator</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField size="small" fullWidth label="Projected Annual Income" type="number"
                                            value={calcForm.projected_annual_income}
                                            onChange={e => setCalcForm(prev => ({ ...prev, projected_annual_income: e.target.value }))}
                                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                            InputLabelProps={{ sx: { fontFamily } }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField size="small" fullWidth label="Projected Deductions" type="number"
                                            value={calcForm.projected_deductions}
                                            onChange={e => setCalcForm(prev => ({ ...prev, projected_deductions: e.target.value }))}
                                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                            InputLabelProps={{ sx: { fontFamily } }} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField size="small" fullWidth label="Credits" type="number"
                                            value={calcForm.credits}
                                            onChange={e => setCalcForm(prev => ({ ...prev, credits: e.target.value }))}
                                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                            InputLabelProps={{ sx: { fontFamily } }} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField size="small" fullWidth label="Withholding" type="number"
                                            value={calcForm.withholding}
                                            onChange={e => setCalcForm(prev => ({ ...prev, withholding: e.target.value }))}
                                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                            InputLabelProps={{ sx: { fontFamily } }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            fullWidth variant="contained"
                                            startIcon={calculating ? null : <CalculateIcon />}
                                            onClick={handleCalculate}
                                            disabled={calculating}
                                            sx={{
                                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                                bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, py: 1.2,
                                            }}
                                        >
                                            {calculating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Calculate'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            {calcResult && (
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2 }}>Results</Typography>

                                    {calcResult.total_estimated_tax != null && (
                                        <Box sx={{ mb: 2, p: 2, borderRadius: '12px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Total Estimated Tax</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '1.8rem', fontWeight: 800, color: '#166534' }}>{formatCurrency(calcResult.total_estimated_tax)}</Typography>
                                        </Box>
                                    )}

                                    {calcResult.safe_harbor_amount != null && (
                                        <Box sx={{ mb: 2, p: 2, borderRadius: '12px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Safe Harbor Amount</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(calcResult.safe_harbor_amount)}</Typography>
                                        </Box>
                                    )}

                                    {calcResult.quarterly_breakdown && calcResult.quarterly_breakdown.length > 0 && (
                                        <>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem', mb: 1 }}>Quarterly Breakdown</Typography>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            {['Quarter', 'Due Date', 'Amount'].map(h => (
                                                                <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                            ))}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {calcResult.quarterly_breakdown.map((q, i) => (
                                                            <TableRow key={i}>
                                                                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>Q{q.quarter}</TableCell>
                                                                <TableCell sx={bodyCellSx}>{formatDate(q.due_date)}</TableCell>
                                                                <TableCell sx={bodyCellSx}>{formatCurrency(q.amount)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    )}
                                </Card>
                            )}
                        </Grid>
                    </Grid>
                </TabPanel>
            </Box>

            {/* Record Payment Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Record Payment</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Paid Amount" type="number" required
                                value={editData.paid_amount}
                                onChange={e => setEditData(prev => ({ ...prev, paid_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Paid Date" type="date" required
                                value={editData.paid_date}
                                onChange={e => setEditData(prev => ({ ...prev, paid_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Payment Method"
                                value={editData.payment_method}
                                onChange={e => setEditData(prev => ({ ...prev, payment_method: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                                placeholder="e.g. ACH, Check" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Confirmation Number"
                                value={editData.confirmation}
                                onChange={e => setEditData(prev => ({ ...prev, confirmation: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSavePayment} variant="contained" disabled={saving || !editData.paid_amount}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
